import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import Backend from 'i18next-http-backend'
import LanguageDetector from 'i18next-browser-languagedetector'
import { useTranslation } from 'react-i18next'
import { NAMESPACES_ENUM, type NamespacesEnum } from './namespaces'

export { NAMESPACES_ENUM }

const DEFAULT_LANG = 'en'

const BASE_ONLY_LOCALES = new Set(['en_us', 'en_uk', 'en_gb'])

const localesBase = (import.meta.env.BASE_URL ?? '/').replace(/\/?$/, '/')

function camelCaseToWords(s = ''): string {
  const result = s.replace(/([A-Z])/g, ' $1')
  return result.charAt(0).toUpperCase() + result.slice(1)
}

export function convertKeyToDefaultValue(i18Key = ''): string {
  let key = i18Key
  if (key.lastIndexOf('--.') > 0) {
    const index = key.lastIndexOf('--.')
    key = key.slice(index + 3)
  }
  if (key.indexOf('--') === 0) {
    key = key.replace(/--/g, '')
    return camelCaseToWords(key)
  }
  return key
}

/** BCP-47 session locale → i18next language code (en-US → en_us, first hyphen only). */
export function toI18nextLanguage(locale: string): string {
  const normalized = locale.trim().toLowerCase().replace('-', '_')
  if (BASE_ONLY_LOCALES.has(normalized)) return DEFAULT_LANG
  return normalized
}

i18next
  .use(LanguageDetector)
  .use(Backend)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANG,
    lowerCaseLng: false,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    preload: [DEFAULT_LANG],
    debug: false,
    ns: NAMESPACES_ENUM.COMMON,
    defaultNS: NAMESPACES_ENUM.COMMON,
    lng: DEFAULT_LANG,
    detection: {
      order: ['localStorage', 'navigator'],
      caches: [],
    },
    interpolation: {
      escapeValue: false,
    },
    backend: {
      loadPath: `${localesBase}locales/{{ns}}/{{lng}}.json`,
    },
    saveMissing: false,
    parseMissingKeyHandler(key: string, defaultValue: string) {
      if (import.meta.env.DEV) {
        console.info('missing i18 key:', key)
      }
      return defaultValue || convertKeyToDefaultValue(key)
    },
  })

export function getI18NInstance(isTranslationEnabled?: boolean) {
  if (!isTranslationEnabled) {
    void i18next.changeLanguage(DEFAULT_LANG)
  }
  return i18next
}

void i18next.loadNamespaces([NAMESPACES_ENUM.COMMON])

export type StringMap = Record<string, unknown>

export function getI18Text(key: string, options?: StringMap): string {
  return i18next.t(key, { ns: NAMESPACES_ENUM.COMMON, ...options }) as string
}

export function getI18TextFromNamespace(
  key: string,
  namespace: string,
  options?: StringMap,
): string {
  return i18next.t(key, { ns: namespace, ...options }) as string
}

export const useTranslationHook = (
  translationList: NamespacesEnum | NamespacesEnum[] | string = NAMESPACES_ENUM.COMMON,
) => useTranslation(translationList)
